import { kioskPunch } from "./api";
import {
  enqueuePunch,
  getPendingPunches,
  markPunchSynced,
  removeSyncedPunches,
  pruneOldPhotos,
} from "./localStore";

let syncing = false;
const listeners = new Set();

export function subscribeSyncStatus(callback) {
  listeners.add(callback);
  return () => listeners.delete(callback);
}

function notify(status) {
  listeners.forEach((cb) => {
    try {
      cb(status);
    } catch (err) {
      console.error("sync status listener error", err);
    }
  });
}

export function isOnline() {
  return typeof navigator !== "undefined" ? navigator.onLine : true;
}

export async function submitPunch(employeeId, action, timestamp = new Date().toISOString()) {
  if (!isOnline()) {
    await enqueuePunch({ employeeId, action, timestamp });
    notify({ syncing: false, pending: (await getPendingPunches()).length });
    const offlineError = new Error("Offline — punch queued for sync");
    offlineError.code = "OFFLINE_QUEUED";
    throw offlineError;
  }

  try {
    const response = await kioskPunch(employeeId, action);
    return response.data;
  } catch (error) {
    if (!error.response) {
      await enqueuePunch({ employeeId, action, timestamp });
      notify({ syncing: false, pending: (await getPendingPunches()).length });
      const queueError = new Error("Network error — punch queued for sync");
      queueError.code = "OFFLINE_QUEUED";
      throw queueError;
    }

    throw error;
  }
}

export async function syncPendingPunches() {
  if (syncing || !isOnline()) {
    return { synced: 0, failed: 0 };
  }

  syncing = true;
  notify({ syncing: true });

  const pending = await getPendingPunches();
  let synced = 0;
  let failed = 0;

  for (const item of pending) {
    try {
      await kioskPunch(item.employeeId, item.action);
      await markPunchSynced(item.id);
      synced += 1;
    } catch (error) {
      failed += 1;
      console.warn("Failed to sync punch", item, error);
    }
  }

  await removeSyncedPunches();
  await pruneOldPhotos(30);

  syncing = false;
  const remaining = await getPendingPunches();
  notify({ syncing: false, pending: remaining.length });

  return { synced, failed };
}

export function initSyncQueue() {
  const handleOnline = () => {
    syncPendingPunches().catch((err) =>
      console.error("Background sync failed", err)
    );
  };

  window.addEventListener("online", handleOnline);

  if (isOnline()) {
    syncPendingPunches().catch((err) =>
      console.error("Initial sync failed", err)
    );
  }

  return () => {
    window.removeEventListener("online", handleOnline);
  };
}
